import { auth } from "../firebase";
import { useEffect, useState, useMemo } from "react";
import {
  LayoutGrid,
  NotebookPen,
  BookOpen,
  BarChart3,
  Calculator,
  CalendarDays,
  MessageSquare,
  Award,
  Crown,
  User,
  Clock // Added Clock for countdown
} from "lucide-react";

export default function Header({ activeTab, userPlan, hasMembership, userData }) {
  /* ================= PAGE CONTEXT ================= */
  const pageConfig = {
    accounts: { title: "Account Overview", note: "Add your accounts here", icon: LayoutGrid },
    addTrade: { title: "Log Trades", note: "Log all of your trades", icon: NotebookPen },
    journal: { title: "Trade Journal", note: "Check your trade history", icon: BookOpen },
    dashboard: { title: "Account Dashboard", note: "View performance summary", icon: BarChart3 },
    lotCalculator: { title: "Lot Calculator", note: "Calculate position size & risk", icon: Calculator },
    news: { title: "News / Event Center", note: "High-impact economic calendar", icon: CalendarDays },
    subscription: { title: "Join A Family 🏆", note: "Become a Founding Member", icon: Award },
    support: { title: "Help & FAQ", note: "Get support or view common questions", icon: MessageSquare }
  };

  const page = pageConfig[activeTab];
  const PageIcon = page?.icon;

  /* ================= MARKET LOGIC ================= */
  const [utcTime, setUtcTime] = useState("");
  const [marketStats, setMarketStats] = useState({
    current: { name: "After Hours", color: "#64748b", status: "Closed" },
    countdowns: []
  });

  useEffect(() => {
    const calculateMarketData = () => {
      const now = new Date();
      const h = now.getUTCHours();
      const m = now.getUTCMinutes();
      setUtcTime(now.toUTCString().split(' ')[4]);

      const markets = [
        { name: "Tokyo", open: 0, close: 8, color: "#10b981" },
        { name: "London", open: 8, close: 16, color: "#38BDF8" },
        { name: "New York", open: 13, close: 21, color: "#38BDF8" }
      ];

      let active = markets.find(mkt => h >= mkt.open && h < mkt.close);
      if (h >= 13 && h < 16) active = { name: "London/NY Overlap", color: "#38BDF8" };
      
      const currentSession = active 
        ? { name: active.name.toUpperCase() + " OPEN", color: active.color }
        : { name: "AFTER HOURS", color: "#64748b" };

      const countdowns = markets.map(mkt => {
        let label = "";
        let timeStr = "";
        if (h >= mkt.open && h < mkt.close) {
          label = `${mkt.name} Closes`;
          const diffH = mkt.close - h - (m > 0 ? 1 : 0);
          const diffM = m > 0 ? 60 - m : 0;
          timeStr = `${diffH}h ${diffM}m`;
        } else {
          label = `${mkt.name} Opens`;
          let diffH = (mkt.open - h + 24) % 24;
          if (m > 0) diffH -= 1;
          const diffM = m > 0 ? 60 - m : 0;
          timeStr = `${diffH}h ${diffM}m`;
        }
        return { label, time: timeStr };
      });

      setMarketStats({ current: currentSession, countdowns });
    };

    calculateMarketData();
    const interval = setInterval(calculateMarketData, 60000);
    return () => clearInterval(interval);
  }, []);

  /* ================= SUBSCRIPTION LOGIC ================= */
  const daysRemaining = useMemo(() => {
    if (!userData?.expiry) return null;
    const diff = new Date(userData.expiry) - new Date();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return days > 0 ? days : 0;
  }, [userData?.expiry]);

  /* ================= USER & MOTIVATION ================= */
  const user = auth.currentUser;
  const username = user?.displayName || user?.email?.split("@")[0] || "Trader";
  const initials = user?.displayName?.split(" ").map(n => n[0]).join("").toUpperCase() || "U";
  
  const messages = ["Protect capital first.", "Patience is your edge.", "Trade less. Execute better."];
  const dailyMessage = useMemo(() => messages[Math.floor(Math.random() * messages.length)], []);

  return (
    <div style={styles.header}>
      {/* LEFT: CONTEXT */}
      <div style={styles.left}>
        <div style={styles.iconBox}>
          {PageIcon && <PageIcon size={20} color={activeTab === 'subscription' ? "#ef4444" : "#38BDF8"} />}
        </div>
        <div>
          <div style={{...styles.pageTitle, color: activeTab === 'subscription' ? "#ef4444" : "#f8fafc"}}>{page?.title}</div>
          <div style={styles.pageNote}>{page?.note}</div>
        </div>
      </div>

      {/* CENTER: GREETING */}
      <div style={styles.center}>
        <div style={styles.greeting}>Hey, <span style={styles.username}>{username}</span></div>
        <div style={styles.motivation}>"{dailyMessage}"</div>
        <div style={styles.statusRow}>
          {hasMembership ? (
            <div style={styles.profitableBadge}><Crown size={12} fill="#ef4444" />PROFITABLE TRADER</div>
          ) : (
            <div style={styles.basicBadge}><User size={12} />BASIC PLAN</div>
          )}
        </div>
      </div>

      {/* RIGHT: MARKET TERMINAL */}
      <div style={styles.right}>
        <div style={styles.marketTerminal}>
          {/* NEW: Countdown injected here */}
          {hasMembership && daysRemaining !== null && (
            <>
              <div style={styles.countdownItem}>
                <span style={styles.mLabel}>Plan Expiring</span>
                <span style={{...styles.mTime, color: daysRemaining < 3 ? '#ef4444' : '#38BDF8'}}>{daysRemaining} Days</span>
              </div>
              <div style={styles.vDivider} />
            </>
          )}

          <div style={styles.activeSession}>
            <div style={{...styles.dot, backgroundColor: marketStats.current.color, boxShadow: `0 0 10px ${marketStats.current.color}`}} />
            <div style={styles.sessionCol}>
              <span style={{...styles.sessionMain, color: marketStats.current.color}}>{marketStats.current.name}</span>
              <span style={styles.utcMini}>UTC {utcTime}</span>
            </div>
          </div>
          <div style={styles.vDivider} />
          <div style={styles.countdownGrid}>
            {marketStats.countdowns.map((m, i) => (
              <div key={i} style={styles.countdownItem}>
                <span style={styles.mLabel}>{m.label}</span>
                <span style={styles.mTime}>{m.time}</span>
              </div>
            ))}
          </div>
          <div style={styles.vDivider} />
          <div style={styles.profileIndicator}>
            <div style={styles.avatar}>{initials}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  header: { padding: "12px 32px", background: "rgba(10, 10, 10, 0.9)", backdropFilter: "blur(15px)", borderBottom: "1px solid rgba(255, 255, 255, 0.05)", display: "grid", gridTemplateColumns: "1fr 1fr 2fr", alignItems: "center", position: "sticky", top: 0, zIndex: 100, height: "95px" },
  left: { display: "flex", alignItems: "center", gap: 14 },
  iconBox: { width: 38, height: 38, borderRadius: 10, background: "rgba(56, 189, 248, 0.08)", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid rgba(56, 189, 248, 0.15)" },
  pageTitle: { fontSize: 16, fontWeight: 700, color: "#f8fafc" },
  pageNote: { fontSize: 11, color: "#64748b" },
  center: { display: "flex", flexDirection: "column", alignItems: "center", gap: 2 },
  greeting: { fontSize: 18, fontWeight: 700, color: "#f8fafc" },
  username: { color: "#38BDF8" },
  motivation: { fontSize: 11, color: "#64748b", fontStyle: "italic", marginBottom: "2px" },
  statusRow: { display: "flex", alignItems: "center" },
  profitableBadge: { display: "flex", alignItems: "center", gap: 6, background: "rgba(239, 68, 68, 0.1)", border: "1px solid rgba(239, 68, 68, 0.3)", color: "#ef4444", padding: "3px 10px", borderRadius: "8px", fontSize: "9px", fontWeight: "800", letterSpacing: "0.5px" },
  basicBadge: { display: "flex", alignItems: "center", gap: 6, background: "rgba(148, 163, 184, 0.1)", border: "1px solid rgba(148, 163, 184, 0.2)", color: "#94a3b8", padding: "3px 10px", borderRadius: "8px", fontSize: "9px", fontWeight: "700" },
  right: { display: "flex", justifyContent: "flex-end" },
  marketTerminal: { display: "flex", alignItems: "center", background: "rgba(255, 255, 255, 0.02)", padding: "6px 16px", borderRadius: "14px", border: "1px solid rgba(255, 255, 255, 0.05)", gap: 20 },
  activeSession: { display: "flex", alignItems: "center", gap: 10 },
  sessionCol: { display: "flex", flexDirection: "column" },
  dot: { width: 6, height: 6, borderRadius: "50%" },
  sessionMain: { fontSize: 11, fontWeight: 800, letterSpacing: "0.5px" },
  utcMini: { fontSize: 10, color: "#64748b", fontFamily: "monospace" },
  vDivider: { width: 1, height: 28, background: "rgba(255,255,255,0.08)" },
  countdownGrid: { display: "flex", gap: 20 },
  countdownItem: { display: "flex", flexDirection: "column" },
  mLabel: { fontSize: 9, color: "#64748b", textTransform: "uppercase", fontWeight: 600 },
  mTime: { fontSize: 11, color: "#f1f5f9", fontWeight: 700, fontFamily: "monospace" },
  profileIndicator: { display: "flex", alignItems: "center" },
  avatar: { width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg, #38BDF8, #0EA5E9)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, color: "#020617" }
};